// components/capture/PhotoRegion.jsx — Anamoria SPA
// v1.0 — C1 extraction from AddMemoryPage v0.3 (July 22, 2026)
//
// Pure relocation (B-2(a), momentedit-4-1-spa-DRAFT-for-review-v0.4.md C1):
// the Photo capture region — chips (R8), PhotoPage v2.9 pipeline values,
// ratified N2/N5 lines — JSX, guard order, and mechanics byte-identical to
// the v0.3 in-page block. The file input ref and inline error state were
// region-internal in effect and are now internal in fact. Entry creation
// (id, object URL) stays with the parent, which owns the photos array and
// URL lifecycle. Styles remain in AddMemoryPage.module.css (draft v0.4 note).
//
// v1.1 — 2.2b: AD card reframe (July 29, 2026). Idle state wrapped in the shared
//   RegionCard; collapsed head click opens the native picker (built behaviour),
//   chips + "Add another photo" render in the open card, error surfaces the card.
//   Chips/validation/guard order unchanged. Helper PHOTO_HELPER — copy pending
//   v0.3 ratification.
//
// Props:
//   photos       — [{ id, url, … }] — parent-owned
//   onAddPhoto   — function(file) — validated File; parent creates the entry
//   onRemovePhoto— function(id)
//   atCap        — boolean
//   saving       — boolean (guard parity with v0.3 handlePhotoSelect)
//   hideRemove   — boolean — A-6 sole-piece rule (edit surfaces only; v1.1,
//                  default false = create-mode byte-identical)
//   className    — string — page entrance-animation class

import { useState, useRef } from 'react';
import styles from '../../pages/AddMemoryPage.module.css';
import RegionCard, { PhotoIcon } from './RegionCard';

// 2.2b helper line (AD mockup; copy pending v0.3 ratification).
const PHOTO_HELPER = 'A photo, if you have one.';

// PhotoPage v2.9 pipeline parity (reuse-first; AD §1.9's 25 MB/HEIC not adopted).
const MAX_PHOTO_SIZE = 10 * 1024 * 1024;
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

// Ratified error strings (copy revisions v0.1 + draft v0.5 closures).
const PHOTO_TOO_LARGE_LINE =
  'We weren’t able to add this photo because the file is very large. A version under 10 MB will work.';
const PHOTO_WRONG_TYPE_LINE =
  'We can’t add this kind of photo. JPEG, PNG, or WebP photos will work.';

export default function PhotoRegion({ photos, onAddPhoto, onRemovePhoto, atCap, saving, hideRemove = false, className = '' }) {
  const [photoError, setPhotoError] = useState('');
  const fileInputRef = useRef(null);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || atCap || saving) return;
    setPhotoError('');
    // N5: defensive type check (the accept filter usually prevents this, but a
    // stray HEIC etc. must get a line, not silence — copy revisions v0.1 gap).
    if (!PHOTO_ACCEPT.split(',').includes(file.type)) {
      setPhotoError(PHOTO_WRONG_TYPE_LINE);
      return;
    }
    // N2: over-size was silently rejected in the scaffold; now surfaced.
    if (file.size > MAX_PHOTO_SIZE) {
      setPhotoError(PHOTO_TOO_LARGE_LINE);
      return;
    }
    onAddPhoto(file);
  };

  const openPicker = () => { if (!atCap && !saving) fileInputRef.current?.click(); };
  // Card opens (shows body) when there are photos or an error to surface;
  // otherwise the collapsed head click opens the native picker (built behaviour).
  const open = photos.length > 0 || !!photoError;

  return (
    <fieldset className={`${styles.region} ${className}`}>
      <legend className={styles.hiddenLabel}>A photo</legend>
      <RegionCard
        icon={<PhotoIcon />}
        title="A photo"
        helper={PHOTO_HELPER}
        open={open}
        disabled={atCap && !open}
        onToggle={openPicker}
      >
        {photos.length > 0 && (
          <div className={styles.chipRow}>
            {photos.map((p) => (
              <div key={p.id} className={styles.photoChipWrap}>
                <img className={styles.photoChip} src={p.url} alt="Photo added to this memory" />
                {!hideRemove && (
                  <button
                    type="button"
                    className={styles.removeLink}
                    onClick={() => onRemovePhoto(p.id)}
                    aria-label="Remove photo from this memory"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {!atCap && (
          <button type="button" className={styles.regionAddMore} onClick={openPicker}>
            {photos.length > 0 ? 'Add another photo' : 'Add a photo'}
          </button>
        )}
        {photoError && (
          <p className={styles.permissionLine} role="alert">{photoError}</p>
        )}
      </RegionCard>
      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        className={styles.fileInput}
        onChange={handlePhotoSelect}
        aria-hidden="true"
        tabIndex={-1}
      />
    </fieldset>
  );
}
